import { Switch, Typography, message } from "antd";
import { BellOutlined, MessageOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import { UserApi } from "../../services";
import { actions as AuthActions } from "../../store/authRedux/actions";
import SectionHeader from "../SectionHeader";
import styles from "./styles.module.css";

/* Both are opt-out: someone who never opens this panel keeps getting everything,
 * which is the behaviour every existing account already has. `?? true` is what
 * carries that over for a profile saved before the fields existed. */
const CHANNELS = [
  {
    key: "sms_notifications_enabled",
    icon: <MessageOutlined />,
    label: "اعلان پیامکی",
    hint: "پیامک‌های مربوط به تسک‌ها، تیکت‌ها و یادآوری‌ها. کد ورود همیشه ارسال می‌شه و به این تنظیم ربطی نداره.",
    onMessage: "اعلان پیامکی روشن شد",
    offMessage: "دیگه پیامکی برات ارسال نمی‌شه",
  },
  {
    key: "push_notifications_enabled",
    icon: <BellOutlined />,
    label: "اعلان روی گوشی و مرورگر",
    hint: "نوتیفیکیشن‌هایی که روی دستگاه‌هات نشون داده می‌شن.",
    onMessage: "اعلان روی دستگاه‌ها روشن شد",
    offMessage: "دیگه نوتیفیکیشنی روی دستگاه‌هات نمیاد",
  },
];

/**
 * ترجیح‌های ارسال اعلان، برای همان کسی که وارد شده.
 *
 * روی `/auth/profile` می‌نشیند نه روی مسیر مدیریت کاربران: این انتخابِ خودِ آدم
 * است، نه چیزی که مدیر برای او تعیین کند — به همین دلیل هم در صفحه‌ی کاربران فقط
 * وقتی نشان داده می‌شود که کاربر دارد پروفایل خودش را می‌بیند.
 *
 * هر کلید بلافاصله ذخیره می‌شود؛ دکمه‌ی «ذخیره»ی پایین فرم، فرمِ اطلاعات پروفایل
 * است و گره‌زدن این دو به هم یعنی کسی که فقط اعلان‌ها را خاموش کرده، ناخواسته
 * بقیه‌ی فیلدها را هم دوباره بفرستد.
 */
const NotificationPreferences = () => {
  const user = useSelector(({ auth }) => auth.user);
  const dispatch = useDispatch();

  const mutation = useMutation(UserApi.updateMyProfile, {
    onSuccess: (res, variables) => {
      dispatch(AuthActions.setUser({ ...user, ...res.data.user }));
      if (variables?.feedback) message.success({ content: variables.feedback });
    },
    onError: () => {
      message.error({ content: "تغییر تنظیمات اعلان انجام نشد" });
    },
  });

  const toggle = (channel, checked) =>
    mutation.mutate({
      body: { [channel.key]: checked },
      feedback: checked ? channel.onMessage : channel.offMessage,
    });

  return (
    <>
      <SectionHeader
        className="flush"
        icon={<BellOutlined />}
        tone="orange"
        title="اعلان‌ها"
      />
      <div className={styles.rows}>
        {CHANNELS.map((channel) => (
          <div className={styles.row} key={channel.key}>
            <span className={styles.text}>
              <span className={styles.label}>
                {channel.icon}
                {channel.label}
              </span>
              <span className={styles.hint}>{channel.hint}</span>
            </span>
            <Switch
              checked={user?.[channel.key] ?? true}
              loading={mutation.isLoading}
              onChange={(checked) => toggle(channel, checked)}
              aria-label={channel.label}
            />
          </div>
        ))}
      </div>
      <Typography.Text className={styles.note}>
        خاموش‌کردن این‌ها فقط جلوی ارسال رو می‌گیره؛ اعلان‌ها همچنان توی زنگوله‌ی
        بالای پنل ثبت می‌شن.
      </Typography.Text>
    </>
  );
};

export default NotificationPreferences;
